import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'
import type { Conditions, DefaultProps } from 'typings'

export type ConditionConfig = {
  name: string
  label: ReactNode
  icon: ReactNode
  onEdit: () => void
  hidden?: boolean
}

type ConditionsConfigMap = Record<string, ConditionConfig>

interface ConditionsContextType {
  conditions: ConditionsConfigMap
  register: (condition: ConditionConfig) => void
  hide: (name: string) => void
  show: (name: string) => void
}

const ConditionsContext = createContext<ConditionsContextType | undefined>(
  undefined
)

export function useConditionsContext() {
  const context = useContext(ConditionsContext)

  if (context === undefined) {
    throw new Error(
      'Your component must be under a ConditionsContextProvider in order to use useConditionsContext hook'
    )
  }

  return context
}

export const ConditionsContextProvider = (props: DefaultProps) => {
  const [conditions, setConditions] = useState<ConditionsConfigMap>({})

  const register = (config: ConditionConfig) => {
    const { name } = config
    const registered = !!conditions[name]

    !registered &&
      setConditions((prev) => ({
        ...prev,
        [name]: { ...config },
      }))
  }

  const hide = useCallback((name: keyof Conditions) => {
    setConditions((prev) => {
      const registered = !!prev[name]

      if (registered) {
        return {
          ...prev,
          [name]: { ...prev[name], hidden: true },
        }
      }

      return prev
    })
  }, [])

  const show = useCallback((name: keyof Conditions) => {
    setConditions((prev) => {
      const registered = !!prev[name]

      if (registered) {
        return {
          ...prev,
          [name]: { ...prev[name], hidden: false },
        }
      }

      return prev
    })
  }, [])

  return (
    <ConditionsContext.Provider value={{ conditions, register, hide, show }}>
      {props.children}
    </ConditionsContext.Provider>
  )
}
